import React, { useState } from 'react';

import { Form } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { LIVING_TYPES } from 'constants/livingTypes';

import {
  convertAccommodationAreasToBaseUnitSystem,
  getApartmentTypeGroupSlug,
  sortAccommodationGroupByBaseRent,
} from './helpers';
import ShopDataModalAccommodationEntry from './ShopDataModalAccommodationEntry';
import ShopDataModalAccommodationEntryLabels from './ShopDataModalAccommodationEntryLabels';
import ShopDataModalAccommodationsAddRoomTypeVariantModal from './ShopDataModalAccommodationsAddRoomTypeVariantModal';
import { useContractContext } from '../../../common/Contract/ContractContext';
import { RENTAL_CONTRACT_SLUG } from '../../../constants';
import { useAppSelector } from '../../../store/hook';
import { ApartmentType, ApartmentTypeGroup } from '../../../types/accommodations';
import ShopDataModalAddButtonPill from '../common/ShopDataModalAddButtonPill';
import ShopDataModalCategoryHeader from '../common/ShopDataModalCategoryHeader';
import { FIELD_CATEGORY } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--line-gray);
`;

const Content = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
  padding: 20px 20px 30px 20px;
`;

const VariantActionsContainer = styled.div`
  display: flex;
  &.centered {
    justify-content: center;
  }
  &:not(.centered) {
    margin-left: 214px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.span`
  font-family: var(--font-bold);
  font-size: 18px;
  color: var(--text-primary);
`;

const Subtitle = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
`;

interface Props {
  onChange: () => void;
  group: ApartmentTypeGroup;
}

const ShopDataModalAccommodationsApartmentTypeGroupContent = ({ group, onChange }: Props) => {
  const apartmentTypes = useAppSelector((state) => state.global.apartmentTypes);
  const { isCommunityCcrc, currentLivingType, unitSource, setCurrentData, handleDataStatusChanged } =
    useShopDataModalContext();
  const { selectedKey } = useContractContext();
  const [roomTypeVariantModalOpen, setRoomTypeVariantModalOpen] = useState(false);
  const [highlightedGroupSlug, setHighlightedGroupSlug] = useState<string>();
  const sortHighlightTimeoutRef = React.useRef<number | undefined>(undefined);
  const form = Form.useFormInstance();
  const showEntranceFee =
    Boolean(selectedKey?.split('|').some((key) => key !== RENTAL_CONTRACT_SLUG)) &&
    isCommunityCcrc &&
    currentLivingType?.id === LIVING_TYPES.INDEPENDENT_LIVING;

  const handleClose = () => {
    onChange();
    setRoomTypeVariantModalOpen(false);
  };

  const handleRemoveAccommodation = (apartmentGroupTypeSlug: string, apartmentTypeSlug: string) => {
    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);
    const currentLivingTypeKey = currentLivingType?.abv;
    setCurrentData((prevState: any) => {
      const newState = { ...prevState };
      const contractKey = selectedKey as string;
      if (newState[FIELD_CATEGORY.ACCOMMODATIONS]?.[currentLivingTypeKey]?.[contractKey]?.[apartmentGroupTypeSlug]) {
        const { [apartmentTypeSlug]: _, ...remainingApartmentTypes } =
          newState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][contractKey][apartmentGroupTypeSlug];

        newState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][contractKey][apartmentGroupTypeSlug] =
          remainingApartmentTypes;
      }

      return newState;
    });
    onChange();
  };

  const handleUnitTypeChange = (groupSlug: string, typeSlug: string, newUnitType: string) => {
    handleDataStatusChanged([FIELD_CATEGORY.ACCOMMODATIONS]);
    const currentLivingTypeKey = currentLivingType?.abv;
    const contractKey = selectedKey as string;

    setCurrentData((prevState: any) => {
      const newState = { ...prevState };
      if (newState[FIELD_CATEGORY.ACCOMMODATIONS]?.[currentLivingTypeKey]?.[contractKey]?.[groupSlug]?.[typeSlug]) {
        newState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][contractKey][groupSlug][typeSlug].unit_type =
          newUnitType;
      }
      return newState;
    });
    onChange();
  };

  const handleAccommodationOrderChange = (groupSlug: string) => {
    const currentLivingTypeKey = currentLivingType.abv;
    const contractKey = selectedKey as string;
    const groupData = form.getFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupSlug]) ?? {};
    const sortedGroupData = sortAccommodationGroupByBaseRent(groupData);
    const sortedStorageGroupData = convertAccommodationAreasToBaseUnitSystem(sortedGroupData, unitSource);
    const previousOrder = Object.keys(groupData);
    const nextOrder = Object.keys(sortedGroupData);
    const orderChanged = previousOrder.some((key, index) => nextOrder[index] !== key);

    form.setFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupSlug], sortedGroupData);

    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.ACCOMMODATIONS]: {
        ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
        [currentLivingTypeKey]: {
          ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey],
          [contractKey]: {
            ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][contractKey],
            [groupSlug]: sortedStorageGroupData,
          },
        },
      },
    }));

    if (orderChanged) {
      window.clearTimeout(sortHighlightTimeoutRef.current);
      setHighlightedGroupSlug(groupSlug);
      sortHighlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedGroupSlug(undefined);
      }, 900);
    }
  };

  React.useEffect(
    () => () => {
      window.clearTimeout(sortHighlightTimeoutRef.current);
    },
    []
  );

  const handleRemoveGroup = () => {
    const groupKey = getApartmentTypeGroupSlug(group.id);
    const currentLivingTypeKey = currentLivingType?.abv;

    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.ACCOMMODATIONS]: {
        ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
        [currentLivingTypeKey]: {
          ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey],
          [selectedKey as string]: Object.fromEntries(
            Object.entries(
              prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey][selectedKey as string]
            ).filter(([key, value]) => key !== groupKey)
          ),
        },
      },
    }));
    onChange();
  };

  return (
    <GroupContainer key={group.id}>
      {roomTypeVariantModalOpen && (
        <ShopDataModalAccommodationsAddRoomTypeVariantModal
          open={roomTypeVariantModalOpen}
          apartmentTypeGroup={group}
          showEntranceFee={showEntranceFee}
          onClose={handleClose}
        />
      )}
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const groupKey = getApartmentTypeGroupSlug(group.id);
          const currentLivingTypeGroupData = getFieldValue([FIELD_CATEGORY.ACCOMMODATIONS, groupKey]);
          const currentLivingTypeGroupEntries = Object.values(currentLivingTypeGroupData ?? {});
          const existingApartmentTypes = Object.keys(currentLivingTypeGroupData ?? {}).map(Number);

          const showAddRoomTypeVariantButton = apartmentTypes.some(
            (item: ApartmentType) => !existingApartmentTypes.includes(item.id)
          );
          const showEmptyMessage = existingApartmentTypes?.length === 0;
          return (
            <>
              <ShopDataModalCategoryHeader
                label={group.name}
                onDelete={showEmptyMessage ? handleRemoveGroup : undefined}
              />
              <Content>
                {showEmptyMessage && (
                  <Message>
                    <Title>There are no room type variants specified</Title>
                    <Subtitle>Use button below to add rooms and their variants.</Subtitle>
                  </Message>
                )}
                {!showEmptyMessage && (
                  <>
                    <ShopDataModalAccommodationEntryLabels showEntranceFeeLabel={showEntranceFee} />
                    {currentLivingTypeGroupEntries.map((entry: any, index) => (
                      <ShopDataModalAccommodationEntry
                        key={entry.apartment_type_id}
                        name={[
                          FIELD_CATEGORY.ACCOMMODATIONS,
                          entry.apartment_type_group_id_slug,
                          entry.apartment_type_id_slug,
                        ]}
                        onRemove={() =>
                          handleRemoveAccommodation(entry.apartment_type_group_id_slug, entry.apartment_type_id_slug)
                        }
                        onUpdateUnitType={(newUnitType) =>
                          handleUnitTypeChange(
                            entry.apartment_type_group_id_slug,
                            entry.apartment_type_id_slug,
                            newUnitType
                          )
                        }
                        onBaseRentBlur={() => handleAccommodationOrderChange(entry.apartment_type_group_id_slug)}
                        onSquareFootageBlur={() => handleAccommodationOrderChange(entry.apartment_type_group_id_slug)}
                        highlightSort={entry.apartment_type_group_id_slug === highlightedGroupSlug}
                        label={`${group.name} ${index + 1}`}
                        description={entry.unit_type}
                        showEntranceFee={showEntranceFee}
                        index={index}
                      />
                    ))}
                  </>
                )}

                <VariantActionsContainer className={classNames({ centered: showEmptyMessage })}>
                  {showAddRoomTypeVariantButton && (
                    <ShopDataModalAddButtonPill
                      label={'Add Variant'}
                      onClick={() => {
                        setRoomTypeVariantModalOpen(true);
                      }}
                      dataTestId="sd-modal-add-room-variant-btn"
                    />
                  )}
                </VariantActionsContainer>
              </Content>
            </>
          );
        }}
      </Form.Item>
    </GroupContainer>
  );
};

export default ShopDataModalAccommodationsApartmentTypeGroupContent;
